/**
 * Admin Dashboard Page
 * 
 * Main admin dashboard showing:
 * - User statistics and analytics
 * - Reading activity overview
 * - System health metrics
 * - Recent user registrations
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  Users, 
  BookOpen, 
  Clock, 
  TrendingUp,
  Calendar,
  Mail,
  Activity,
  Download,
  GraduationCap,
  BarChart3,
  School,
  Bell
} from "lucide-react";
import { client } from "@/lib/api";
import { toast } from "sonner";

interface UserStats {
  totalUsers: number;
  newUsersToday: number;
  activeUsers: number;
  totalReadingTime: number;
  totalSessions: number;
  averageEngagementScore: number;
}

interface TopUser {
  userId: string;
  userName: string;
  userEmail: string;
  engagementScore: number;
  totalSessions: number;
  totalReadingTime: number;
}

interface RecentUser {
  id: string;
  name: string;
  email: string;
  created_at: string;
  subscription_status: string;
}

interface EducatorInsights {
  activeEducators: number;
  classBreakdown: { grade_level: string; school_name: string; class_count: number }[];
  domainAverages: Record<string, number> & { total_checkins?: number };
  domainTrends: { month: string; checkin_count: number; [key: string]: any }[];
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    newUsersToday: 0,
    activeUsers: 0,
    totalReadingTime: 0,
    totalSessions: 0,
    averageEngagementScore: 0
  });
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [educatorInsights, setEducatorInsights] = useState<EducatorInsights | null>(null);
  const [notifications, setNotifications] = useState<{ id: string; message: string; created_at: string }[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadNotifications();
  }, []);

  // Add refresh function to manually reload data when needed
  const refreshDashboard = async () => {
    setLoading(true);
    await loadDashboardData();
    toast.success("Dashboard data refreshed");
  };

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;
      const res = await fetch("/api/admin/notifications", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) { console.error("Failed to load notifications:", e); }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;
      await fetch("/api/admin/notifications/read", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) { console.error("Failed to mark notifications read:", e); }
  };

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      // FIXED: Load consistent analytics summary with enhanced error handling and data validation
      try {
        const analyticsResponse = await fetch('/api/book-access/analytics/summary');
        
        if (analyticsResponse.ok) {
          const analyticsData = await analyticsResponse.json();
          
          // Enhanced data validation and consistency
          if (analyticsData && typeof analyticsData === 'object') {
            // Ensure all numeric values are valid and stable
            const validatedStats = {
              totalUsers: Number(analyticsData.totalUsers) || 0,
              newUsersToday: Number(analyticsData.newUsersToday) || 0,
              activeUsers: Number(analyticsData.activeUsers) || 0,
              totalReadingTime: Number(analyticsData.totalReadingTime) || 0,
              totalSessions: Number(analyticsData.totalSessions) || 0,
              averageEngagementScore: Number(analyticsData.averageEngagementScore) || 0
            };
            
            setStats(validatedStats);
            setTopUsers(Array.isArray(analyticsData.topUsers) ? analyticsData.topUsers : []);
            
            console.log("📊 Loaded CONSISTENT user-account-based analytics:", {
              totalUsers: validatedStats.totalUsers,
              totalSessions: validatedStats.totalSessions,
              activeUsers: validatedStats.activeUsers,
              averageEngagement: validatedStats.averageEngagementScore,
              dataConsistency: "✅ Validated",
              timestamp: new Date().toISOString()
            });
          } else {
            console.warn("⚠️ Invalid analytics data structure received");
            // Set fallback data to prevent undefined displays
            setStats({
              totalUsers: 0,
              newUsersToday: 0,
              activeUsers: 0,
              totalReadingTime: 0,
              totalSessions: 0,
              averageEngagementScore: 0
            });
          }
        } else {
          console.warn("⚠️ Analytics endpoint returned error:", analyticsResponse.status);
          // Set fallback data to prevent undefined displays
          setStats({
            totalUsers: 0,
            newUsersToday: 0,
            activeUsers: 0,
            totalReadingTime: 0,
            totalSessions: 0,
            averageEngagementScore: 0
          });
        }
      } catch (analyticsError) {
        console.warn("⚠️ Analytics data not available:", analyticsError);
        // Set fallback data to prevent undefined displays
        setStats({
          totalUsers: 0,
          newUsersToday: 0,
          activeUsers: 0,
          totalReadingTime: 0,
          totalSessions: 0,
          averageEngagementScore: 0
        });
      }

      // Load admin dashboard data (for recent users)
      try {
        const response = await client.api.admin.dashboard.$get({}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setRecentUsers(data.recentUsers || []);
        }
      } catch (adminError) {
        console.log("Admin dashboard endpoint not available, using analytics data only");
      }

      // Load educator insights
      try {
        const insightsResponse = await fetch('/api/educator/admin/insights', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (insightsResponse.ok) {
          const insightsData = await insightsResponse.json();
          setEducatorInsights(insightsData);
        }
      } catch (insightsError) {
        console.log("Educator insights not available:", insightsError);
      }
      
    } catch (error) {
      console.error("Dashboard load error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const exportEducatorData = () => {
    if (!educatorInsights) return;
    const DOMAIN_LABELS: Record<string, string> = {
      emotional_awareness: "Colorful Chameleon",
      self_regulation: "Steady Oak",
      courage: "Brave Thing",
      kindness: "Warm Hearted",
      gratitude: "Grateful Heart",
      sharing_repair: "Bridge Builder",
      mattering: "You Matter",
    };
    const rows = [
      ["Dira Educator Insights Export", new Date().toLocaleDateString()],
      [],
      ["Active Educators", educatorInsights.activeEducators],
      ["Total Check-ins", educatorInsights.domainAverages.total_checkins || 0],
      [],
      ["Domain Averages (1-3 scale)"],
      ["Domain", "Average Rating"],
      ...Object.entries(DOMAIN_LABELS).map(([key, label]) => [
        label, educatorInsights.domainAverages[key] || "—"
      ]),
      [],
      ["School & Grade Breakdown"],
      ["School", "Grade Level", "Classes"],
      ...educatorInsights.classBreakdown.map(r => [r.school_name || "—", r.grade_level || "—", r.class_count]),
      [],
      ["Domain Trends Over Time"],
      ["Month", ...Object.values(DOMAIN_LABELS), "Check-ins"],
      ...educatorInsights.domainTrends.map(r => [
        new Date(r.month).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        ...Object.keys(DOMAIN_LABELS).map(k => r[k] || "—"),
        r.checkin_count
      ]),
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dira-educator-insights-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const exportUserData = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      const response = await client.api.admin["export-users"].$get({}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create CSV content
        const csvContent = [
          "Name,Email,Registration Date,Subscription Status,Last Active",
          ...data.users.map((user: any) => 
            `"${user.name}","${user.email}","${user.created_at}","${user.subscription_status}","${user.last_active || 'Never'}"`
          )
        ].join("\n");
        
        // Download CSV file
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `brave-things-users-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("User data exported successfully");
      } else {
        toast.error("Failed to export user data");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export user data");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">User analytics and platform overview</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => { setShowNotifications(p => !p); if (unreadCount > 0) markAllRead(); }}
              className="relative p-2 rounded-xl border border-forest-200 text-forest-600 hover:bg-forest-50 transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-coral-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-forest-100 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-forest-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-forest-800">New Signups</p>
                  <button onClick={() => setShowNotifications(false)} className="text-xs text-forest-400 hover:text-forest-600">Close</button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-sm text-forest-400 text-center py-6">No notifications yet</p>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id} className="px-4 py-3 border-b border-forest-50 last:border-0">
                        <p className="text-sm text-forest-700">{n.message}</p>
                        <p className="text-xs text-forest-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <Button onClick={exportUserData} className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export User Data
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
            <Users className="w-4 h-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{stats.totalUsers}</div>
            <p className="text-xs text-gray-500 mt-1">
              +{stats.newUsersToday} today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Readers</CardTitle>
            <BookOpen className="w-4 h-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{stats.activeUsers}</div>
            <p className="text-xs text-gray-500 mt-1">
              Currently reading
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Reading Time</CardTitle>
            <Clock className="w-4 h-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{Math.round(stats.totalReadingTime / 60)}h</div>
            <p className="text-xs text-gray-500 mt-1">
              Total minutes read
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Engagement</CardTitle>
            <TrendingUp className="w-4 h-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-forest-600">{stats.averageEngagementScore}%</div>
            <p className="text-xs text-gray-500 mt-1">
              Average user engagement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout for Analytics */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Engaged Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-forest-600" />
              Top Engaged Readers
            </CardTitle>
            <CardDescription>
              Users with highest engagement scores (user-account-based analytics)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No analytics data available</p>
              ) : (
                topUsers.map((user, index) => (
                  <div key={user.userId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-golden-100 rounded-full flex items-center justify-center">
                        <span className="text-golden-700 font-bold text-sm">
                          #{index + 1}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.userName}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {user.totalSessions} sessions
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xl font-bold text-forest-600">{user.engagementScore}%</div>
                      <div className="text-xs text-gray-500">
                        {user.totalReadingTime}min total
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Recent User Registrations
            </CardTitle>
            <CardDescription>
              New users who joined the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No recent users found</p>
              ) : (
                recentUsers.slice(0, 5).map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-forest-100 rounded-full flex items-center justify-center">
                        <span className="text-forest-700 font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <Badge variant={user.subscription_status === "premium" ? "default" : "secondary"}>
                        {user.subscription_status}
                      </Badge>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-600" />
            User-Account-Based Analytics System
          </CardTitle>
          <CardDescription>
            Analytics are now tied to user accounts for continuous tracking across sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600">{stats.totalSessions}</div>
              <div className="text-sm text-green-700">Total Sessions</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">{Math.round(stats.totalReadingTime / 60)}h</div>
              <div className="text-sm text-blue-700">Reading Time</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600">{stats.averageEngagementScore}%</div>
              <div className="text-sm text-purple-700">Avg Engagement</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-2xl font-bold text-orange-600">✅</div>
              <div className="text-sm text-orange-700">Token Persistence</div>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-cream-50 rounded-lg border border-forest-200">
            <h4 className="font-semibold text-forest-800 mb-2">✨ New Analytics Features:</h4>
            <ul className="text-sm text-forest-700 space-y-1">
              <li>• <strong>User Identity Tracking:</strong> Analytics linked to user accounts, not tokens</li>
              <li>• <strong>Continuous Sessions:</strong> Data accumulates across all reading sessions</li>
              <li>• <strong>Token Persistence:</strong> Tokens persist in development for uninterrupted tracking</li>
              <li>• <strong>Engagement Scoring:</strong> Real-time calculation of user engagement levels</li>
              <li>• <strong>Reading Progression:</strong> Complete journey tracking over time</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      {/* Educator Insights */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-forest-600" />
                Educator Insights
              </CardTitle>
              <CardDescription>Anonymous aggregated data from all educator dashboards</CardDescription>
            </div>
            <Button
              onClick={exportEducatorData}
              disabled={!educatorInsights}
              variant="outline"
              className="flex items-center gap-2 border-forest-200 text-forest-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!educatorInsights ? (
            <p className="text-gray-500 text-center py-8">No educator data available yet</p>
          ) : (
            <>
              {/* Top stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-forest-50 rounded-lg border border-forest-200">
                  <div className="text-2xl font-bold text-forest-700">{educatorInsights.activeEducators}</div>
                  <div className="text-sm text-forest-600">Active Educators</div>
                </div>
                <div className="text-center p-4 bg-golden-50 rounded-lg border border-golden-200">
                  <div className="text-2xl font-bold text-golden-700">{educatorInsights.domainAverages.total_checkins || 0}</div>
                  <div className="text-sm text-golden-600">Total Check-ins</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-700">{educatorInsights.classBreakdown.length}</div>
                  <div className="text-sm text-blue-600">Class Groups</div>
                </div>
              </div>

              {/* Domain averages */}
              {educatorInsights.domainAverages.total_checkins ? (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Domain Averages
                    <span className="text-xs font-normal text-gray-400">(1–3 scale, across all check-ins)</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: "emotional_awareness", label: "Colorful Chameleon" },
                      { key: "self_regulation", label: "Steady Oak" },
                      { key: "courage", label: "Brave Thing" },
                      { key: "kindness", label: "Warm Hearted" },
                      { key: "gratitude", label: "Grateful Heart" },
                      { key: "sharing_repair", label: "Bridge Builder" },
                      { key: "mattering", label: "You Matter" },
                    ].map(domain => {
                      const val = Number(educatorInsights.domainAverages[domain.key]) || 0;
                      const pct = Math.round((val / 3) * 100);
                      return (
                        <div key={domain.key} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">{domain.label}</p>
                          <p className="text-xl font-bold text-forest-700">{val.toFixed(1)}</p>
                          <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full">
                            <div className="h-1.5 bg-forest-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* School & grade breakdown */}
              {educatorInsights.classBreakdown.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <School className="w-4 h-4" /> School & Grade Breakdown
                  </h4>
                  <div className="space-y-2">
                    {educatorInsights.classBreakdown.map((row, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-800">{row.school_name || "Unknown School"}</span>
                          <span className="text-gray-400 mx-2">·</span>
                          <span className="text-gray-500 text-sm">{row.grade_level || "Unknown Grade"}</span>
                        </div>
                        <Badge variant="secondary">{row.class_count} {Number(row.class_count) === 1 ? "class" : "classes"}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Domain trends over time */}
              {educatorInsights.domainTrends.length > 1 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Domain Ratings Over Time
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 pr-4 text-gray-500 font-medium">Month</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Chameleon</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Oak</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Courage</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Kindness</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Gratitude</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Bridge</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Mattering</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Check-ins</th>
                        </tr>
                      </thead>
                      <tbody>
                        {educatorInsights.domainTrends.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 pr-4 text-gray-700 font-medium">
                              {new Date(row.month).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                            </td>
                            {["emotional_awareness","self_regulation","courage","kindness","gratitude","sharing_repair","mattering"].map(k => (
                              <td key={k} className="py-2 px-2 text-center text-forest-700 font-medium">
                                {Number(row[k]).toFixed(1)}
                              </td>
                            ))}
                            <td className="py-2 px-2 text-center text-gray-500">{row.checkin_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </motion.div>
  );
}
